import * as amplitude from "@amplitude/analytics-browser";
import { MenuItem } from "@chakra-ui/react";
import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useOrganization } from "src/hooks/useOrganization";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import * as FormTemplate from "src/services/formTemplate";
import { OrgAdmin } from "src/services/url";
import * as GQL from "src/types/graphql";
import { AdvancedViewSettingsState } from "./index";

type Props = {
  formTemplate: GQL.FormTemplateFragment;
  showResetToDefaultColumns?: boolean;
  onResetToDefaultColumns: () => void;
};

export const AdvancedViewSettings: React.FC<Props> = ({
  formTemplate,
  showResetToDefaultColumns,
  onResetToDefaultColumns,
}) => {
  const navigate = useNavigate();
  const organization = useOrganization();
  const [searchParams] = useSearchParams();
  const formTemplateSections = FormTemplate.fromGQL(formTemplate).sections;
  // Use the formTemplateId from the URL params, which differs from formTemplate.id in the Dropoff Forms tab
  const { formTemplateId } = useParams();
  const schoolAdminCheck = useSchoolAdmin();

  const handleAdvancedViewSelection = React.useCallback(() => {
    navigate(
      organization
        .map((org) =>
          OrgAdmin.Forms.advancedViewSettings({
            organization: org,
            formTemplateId: formTemplateId ?? "",
            params: searchParams.toString(),
          })
        )
        .withDefault("#"),
      {
        state: {
          questionIdsAndLabelsInOrder:
            FormTemplate.Question.getQuestionIdsAndLabelsInOrder(
              formTemplateSections,
              schoolAdminCheck.isSchoolAdmin
                ? {
                    filterBy: "school",
                    schoolIdSet: new Set(schoolAdminCheck.allSchoolIds),
                  }
                : { filterBy: "none" }
            ),
          disclaimerIdsAndLabels: formTemplateSections
            .filter((section) => section.type === "DisclaimerSection")
            .map((section) => ({
              id: section.id,
              label: section.title,
            })),
          formTemplateConfig: {
            isLotteryEnabled: formTemplate?.lottery_offers_enabled ?? false,
            isRankingEnabled:
              formTemplate?.sections.find(
                (section) => section.type === "SchoolRankingSection"
              )?.schools_ranking_section?.ranking_enabled ?? false,
          },
        } as AdvancedViewSettingsState,
      }
    );
  }, [
    formTemplate?.lottery_offers_enabled,
    formTemplate?.sections,
    formTemplateId,
    formTemplateSections,
    navigate,
    organization,
    schoolAdminCheck.allSchoolIds,
    schoolAdminCheck.isSchoolAdmin,
    searchParams,
  ]);

  return (
    <>
      {showResetToDefaultColumns && (
        <MenuItem
          key="reset-to-default-columns"
          gap={2}
          onClick={() => {
            amplitude.track("Clicked Reset to default columns");
            onResetToDefaultColumns();
          }}
        >
          Reset to default columns
        </MenuItem>
      )}
      <MenuItem
        key="advanced-column-selection"
        gap={2}
        onClick={() => {
          amplitude.track("Clicked Advanced view settings");
          handleAdvancedViewSelection();
        }}
      >
        Advanced view settings
      </MenuItem>
    </>
  );
};
